import { useNavigate } from "react-router-dom";
import { useState } from "react";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import WhatsAppTemplatePreview from "../components/WhatsAppTemplatePreview";
import PageHeader from "../components/common/PageHeader";
import { toast } from "react-toastify";
import EmojiPicker from "emoji-picker-react";

const CreateTemplatePage = () => {
  const navigate = useNavigate();

  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [languageCode, setLanguageCode] = useState("en_US");
  const [headerType, setHeaderType] = useState("none");
  const [headerValue, setHeaderValue] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [buttonValue, setButtonValue] = useState("");
  const [buttonType, setButtonType] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [footerText, setFooterText] = useState("");
  const [addStopFooter, setAddStopFooter] = useState(false);
  const [addStopButton, setAddStopButton] = useState(false);

  const STOP_FOOTER = "To stop receiving these updates, reply STOP";

  const handleStopFooterToggle = (checked) => {
    setAddStopFooter(checked);
    if (checked) setFooterText(STOP_FOOTER);
    else setFooterText("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const url = "/upload-image-create-template";

    try {
      if (headerType === "none" || headerType === "text") {
        const payload = {
          templateName,
          language: languageCode,
          category: category,
          headerType,
          headerValue: headerType === "text" ? headerValue : "",
          bodyText,
          footerText,
          buttonType,
          buttonText,
          buttonValue,
          stopButton: addStopButton,
        };

        await api.post(url, payload);
        toast.success("Template created successfully!");
        navigate("/templateList");
      } else {
        // Validate file for image type
        if (headerType === "image") {
          if (!headerValue) {
            toast.error("Please upload an image file.");
            setIsLoading(false);
            return;
          }
          const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
          if (!allowedTypes.includes(headerValue.type)) {
            toast.error("Only JPEG and PNG images are allowed.");
            setIsLoading(false);
            return;
          }
          if (headerValue.size > 5 * 1024 * 1024) {
            toast.error("Image file must be under 5MB. Please compress or resize the image.");
            setIsLoading(false);
            return;
          }
        }
        if (headerType === "video") {
          if (!headerValue) {
            toast.error("Please upload a video file.");
            setIsLoading(false);
            return;
          }
          const allowedVideoTypes = ["video/mp4"];
          if (!allowedVideoTypes.includes(headerValue.type)) {
            toast.error("Only MP4 videos are allowed.");
            setIsLoading(false);
            return;
          }
          if (headerValue.size > 16 * 1024 * 1024) {
            toast.error("Video file must be under 16MB.");
            setIsLoading(false);
            return;
          }
        }

        // Send as multipart FormData — file goes as binary (no base64 inflation)
        const formData = new FormData();
        formData.append("templateName", templateName);
        formData.append("language", languageCode);
        formData.append("category", category);
        formData.append("headerType", headerType);
        formData.append("bodyText", bodyText);
        if (footerText) formData.append("footerText", footerText);
        if (buttonType) formData.append("buttonType", buttonType);
        if (buttonText) formData.append("buttonText", buttonText);
        if (buttonValue) formData.append("buttonValue", buttonValue);
        formData.append("stopButton", addStopButton ? "true" : "false");
        formData.append("headerFile", headerValue);

        await api.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Template with media created successfully!");
        navigate("/templateList");
      }
    } catch (error) {
      console.error("Error creating template:", error.message);
      toast.error(error.message || "Failed to create template. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleHeaderTextChange = (e) => {
    let input = e.target.value;

    // Remove emojis and asterisks
    input = input.replace(/[\p{Emoji_Presentation}\p{Emoji}\u200d*]+/gu, "");

    // Limit to 60 characters
    if (input.length > 60) {
      input = input.slice(0, 60);
    }

    setHeaderValue(input);
  };

  const handleHeaderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderValue(file); // or setHeaderFile(file), depending on how you're storing it
    }
  };
  const applyFormatting = (type) => {
    let markdown = bodyText;
    const selectedText = window.getSelection().toString();
    if (type === "bold") markdown += `*${selectedText || "bold"}*`;
    if (type === "italic") markdown += `_${selectedText || "italic"}_`;
    if (type === "strike") markdown += `~${selectedText || "strike"}~`;
    setBodyText(markdown);
  };

  const insertEmoji = (emojiObject) => {
    setBodyText((prev) => prev + emojiObject.emoji);
  };

  return (
    <div className="bg-gray-50 h-[calc(100vh-64px)] flex flex-col overflow-hidden">
      {/* Page header — never scrolls */}
      <div className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-gray-200 bg-gray-50">
        <PageHeader
          title="Create Message Template"
          subtitle="Define your WhatsApp message template including header and body."
        />
      </div>

      {/* Two-panel area */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4 sm:gap-6 p-4 sm:p-6 overflow-y-auto lg:overflow-hidden">
        {/* Form column — scrolls on its own */}
        <div className="lg:flex-1 lg:min-w-0 lg:overflow-y-auto lg:pr-1">
        {/* Form Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Template Configuration
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Template Name, Language, Category */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  placeholder="Enter template name"
                  value={templateName}
                  onChange={(e) => {
                    const formatted = e.target.value
                      .toLowerCase() // convert to lowercase
                      .replace(/\s+/g, "_"); // replace spaces with underscores
                    setTemplateName(formatted);
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={languageCode}
                  onChange={(e) => setLanguageCode(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                >
                  <option value="en_US">English (US)</option>
                  <option value="ml">Malayalam (India)</option>
                  <option value="hi_IN">Hindi (India)</option>
                  <option value="ar">Arabic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                >
                  <option value="MARKETING">MARKETING</option>
                  <option value="UTILITY">UTILITY</option>
                </select>
              </div>
            </div>

            {/* Header Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Header Settings
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Header Type
                  </label>
                  <select
                    value={headerType}
                    onChange={(e) => {
                      setHeaderType(e.target.value);
                      setHeaderValue("");
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  >
                    <option value="none">None</option>
                    <option value="text">Text</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                  </select>
                </div>

                <div>
                  {headerType === "text" && (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Header Text
                      </label>
                      <input
                        type="text"
                        placeholder="Enter header text"
                        value={headerValue}
                        onChange={handleHeaderTextChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                      <p className="text-sm text-gray-500 mt-1 text-right">
                        {headerValue.length} / 60 characters
                      </p>
                    </>
                  )}

                  {["image", "video", "document"].includes(headerType) && (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload File
                      </label>
                      <input
                        type="file"
                        accept={
                          headerType === "image"
                            ? "image/jpeg,image/jpg,image/png"
                            : headerType === "video"
                            ? "video/mp4"
                            : ".pdf,.doc,.docx"
                        }
                        onChange={handleHeaderFileChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Body Text Section */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Body Text *
              </label>
              <div className="relative">
                <textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  rows={4}
                  placeholder="Enter your message body..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 resize-none"
                />

                {/* Toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPicker(!showPicker)}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition duration-200"
                    >
                      😀
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormatting("bold")}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition duration-200 font-bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormatting("italic")}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition duration-200 italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormatting("strike")}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition duration-200 line-through"
                    >
                      S
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    {bodyText.length} / 1024
                  </p>
                </div>

                {/* Emoji Picker */}
                {showPicker && (
                  <div className="absolute right-0 top-full mt-2 z-50">
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg">
                      <EmojiPicker onEmojiClick={insertEmoji} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Footer Settings
              </h3>

              {/* Opt-out options */}
              <div className="space-y-2 mb-4">
                <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={addStopFooter}
                    onChange={(e) => handleStopFooterToggle(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Add opt-out footer text</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Adds: <span className="italic text-gray-600">"{STOP_FOOTER}"</span>
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={addStopButton}
                    onChange={(e) => setAddStopButton(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Add "STOP" quick reply button</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Adds a one-tap <span className="font-semibold text-gray-600">STOP</span> button — customer taps it to opt out
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Footer Text <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter footer text (optional)"
                  maxLength={60}
                  value={footerText}
                  onChange={(e) => {
                    setFooterText(e.target.value.slice(0, 60));
                    setAddStopFooter(false);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />
                <p className="text-sm text-gray-500 mt-1 text-right">
                  {footerText.length} / 60 characters
                </p>
              </div>
            </div>

            {/* Button Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Button Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Type
                  </label>
                  <select
                    value={buttonType}
                    onChange={(e) => {
                      setButtonType(e.target.value);
                      setButtonValue("");
                      setButtonText("");
                    }}
                    className="w-full lg:w-1/3 border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  >
                    <option value="none">None</option>
                    <option value="URL">URL</option>
                    <option value="PHONE_NUMBER">Phone Number</option>
                  </select>
                </div>

                {buttonType === "URL" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button Text
                      </label>
                      <input
                        type="text"
                        placeholder="Enter button text"
                        value={buttonText}
                        onChange={(e) => setButtonText(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                      <p className="text-sm text-gray-500 mt-1 text-right">
                        {buttonText.length} / 25 characters
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL
                      </label>
                      <input
                        type="text"
                        placeholder="https://example.com"
                        value={buttonValue}
                        onChange={(e) => setButtonValue(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                      <p className="text-sm text-gray-500 mt-1 text-right">
                        {buttonValue.length} / 2000 characters
                      </p>
                    </div>
                  </div>
                )}

                {buttonType === "PHONE_NUMBER" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button Text
                      </label>
                      <input
                        type="text"
                        placeholder="Enter button text"
                        value={buttonText}
                        onChange={(e) => setButtonText(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                      <p className="text-sm text-gray-500 mt-1 text-right">
                        {buttonText.length} / 25 characters
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        placeholder="+1234567890"
                        value={buttonValue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\+?\d*$/.test(input)) {
                            setButtonValue(input);
                          }
                        }}
                        pattern="^\+?\d{10,15}$"
                        title="Enter a valid phone number, e.g. +911234567890"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                      <p className="text-sm text-gray-500 mt-1 text-right">
                        {buttonValue.length} / 20 characters
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <LoadingSpinner
                      size="sm"
                      color="white"
                      text="Creating..."
                    />
                  ) : (
                    "Save Template"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
        </div>{/* end form column */}

        {/* Preview column — fixed, never scrolls */}
        <div className="hidden lg:flex lg:flex-1 lg:min-w-0 lg:overflow-hidden">
          <WhatsAppTemplatePreview
            templateName={templateName}
            languageCode={languageCode}
            category={category}
            headerType={headerType}
            headerValue={headerValue}
            bodyText={bodyText}
            footerText={footerText}
            buttonType={buttonType}
            buttonText={buttonText}
            stopButton={addStopButton}
          />
        </div>
      </div>{/* end two-panel */}
    </div>
  );
};

export default CreateTemplatePage;
